import type { ReportTemplate, ReportConfig } from '@/types/custom-reports';

// ============================================================================
// REPORT TEMPLATES
// ============================================================================

export const REPORT_TEMPLATES: ReportTemplate[] = [
  {
    id: 'monthly-cashflow',
    name: 'Fluxo de Caixa Mensal',
    description: 'Visão completa das entradas e saídas do mês, agrupadas por categoria',
    icon: '📊',
    config: {
      dataSources: ['transactions', 'categories'],
      dateRange: { type: 'this_month' },
      filters: {
        transactionType: 'all',
        status: 'all',
      },
      groupBy: 'category',
      compareWith: { enabled: false },
      metrics: { amount: 'sum' },
      visualizationType: 'bar',
      chartConfig: {
        showLegend: true,
        showDataLabels: true,
      },
    },
  },
  {
    id: 'expenses-by-center',
    name: 'Despesas por Centro de Custo',
    description: 'Análise de gastos por departamento',
    icon: '🏢',
    config: {
      dataSources: ['transactions', 'cost_centers'],
      dateRange: { type: 'this_month' },
      filters: {
        transactionType: 'expense',
        status: 'paid',
      },
      groupBy: 'cost_center',
      compareWith: { enabled: false },
      metrics: { amount: 'sum' },
      visualizationType: 'pie',
      chartConfig: {
        showLegend: true,
        showDataLabels: true,
      },
    },
  },
  {
    id: 'receivables-summary',
    name: 'Resumo de Contas a Receber',
    description: 'Status dos recebimentos por cliente',
    icon: '💰',
    config: {
      dataSources: ['transactions', 'clients'],
      dateRange: { type: 'this_month' },
      filters: {
        transactionType: 'income',
        status: 'pending',
      },
      groupBy: 'client',
      compareWith: { enabled: false },
      metrics: { amount: 'sum' },
      visualizationType: 'table',
    },
  },
  {
    id: 'income-vs-expense',
    name: 'Receitas vs Despesas',
    description: 'Comparativo mensal de receitas e despesas',
    icon: '⚖️',
    config: {
      dataSources: ['transactions', 'categories'],
      dateRange: { type: 'this_month' },
      filters: {
        transactionType: 'all',
        status: 'all',
      },
      groupBy: 'category',
      compareWith: { enabled: true, type: 'previous_period' },
      metrics: { amount: 'sum' },
      visualizationType: 'bar',
      chartConfig: {
        showLegend: true,
        showDataLabels: true,
      },
    },
  },
  {
    id: 'cash-flow-trend',
    name: 'Tendência de Fluxo de Caixa',
    description: 'Evolução do fluxo de caixa ao longo dos meses',
    icon: '📈',
    config: {
      dataSources: ['transactions'],
      dateRange: { type: 'custom', start: '', end: '' }, // User must set date range
      filters: {
        transactionType: 'all',
        status: 'paid',
      },
      groupBy: 'month',
      compareWith: { enabled: false },
      metrics: { amount: 'sum' },
      visualizationType: 'line',
      chartConfig: {
        showLegend: true,
        showDataLabels: false,
      },
    },
  },
];

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Get all available report templates
 */
export function useReportTemplates() {
  return REPORT_TEMPLATES;
}

/**
 * Get a specific template by ID
 */
export function useReportTemplate(id: string) {
  return REPORT_TEMPLATES.find(t => t.id === id);
}

/**
 * Get template config by ID (useful for pre-filling the builder)
 */
export function useTemplateConfig(id: string): ReportConfig | null {
  const template = REPORT_TEMPLATES.find(t => t.id === id);
  return template?.config || null;
}

/**
 * Get templates grouped by category
 */
export function useGroupedTemplates() {
  return {
    'Fluxo de Caixa': REPORT_TEMPLATES.filter(t =>
      t.id === 'monthly-cashflow' || t.id === 'cash-flow-trend' || t.id === 'income-vs-expense'
    ),
    'Despesas': REPORT_TEMPLATES.filter(t => t.id === 'expenses-by-center'),
    'Recebimentos': REPORT_TEMPLATES.filter(t => t.id === 'receivables-summary'),
  };
}

/**
 * Get featured templates (showcased on gallery)
 */
export function useFeaturedTemplates() {
  return REPORT_TEMPLATES.slice(0, 3);
}
